/**
 * The catalogue of entity types a MIC QR label can identify.
 *
 * Two forms of the same name travel through the app and both are needed:
 *   - code form  'RawMaterial'   — what the generator, the widget configuration and Sun's
 *                                 qrcodetemplate EntityType use.
 *   - wire form  'RAWMATERIAL'   — what the payload carries and what parseQrPayload returns,
 *                                 since the QR alphanumeric charset has no separators.
 *
 * Kept as a zero-import leaf so any consumer (a MOS widget, a dialog, a pure mapping module) can
 * read it without dragging UI or API code along.
 */

// Order is the one shown in the widget configuration: the two most scanned first.
export const QR_ENTITY_TYPES = ['Part', 'RawMaterial', 'FabricationOrder', 'Order', 'Tool', 'Fixture']

// Wire form → translatable label. t_ echoes missing keys, so an unknown type degrades to itself.
export const QR_ENTITY_LABELS = {
  PART: 'Part',
  RAWMATERIAL: 'Raw Material',
  FABRICATIONORDER: 'Fabrication Order',
  ORDER: 'Order',
  TOOL: 'Tool',
  FIXTURE: 'Fixture'
}

// Same normalisation the payload builder applies to an entity name: no dots, uppercase.
export const wireEntityType = (entityType) => String(entityType || '').replace(/\./g, '').toUpperCase()

// The i18n key for a type given in either form; falls back to the input so nothing renders blank.
export const entityTypeLabelKey = (entityType) =>
  QR_ENTITY_LABELS[wireEntityType(entityType)] || String(entityType || '')

/**
 * A scanned element read at the wrong place is reported by Sun as a single FOREIGN verdict, but the
 * context the verdict was answered against is not the same for every type — so neither is the
 * sentence, nor whether naming the station's fabrication order explains anything:
 *
 *   - a raw-material lot is bound to the WAREHOUSE of a machine. The card's Location field already
 *     says which machine holds it, and the running order has nothing to do with it.
 *   - a part is bound to its FABRICATION ORDER. The match is at order level, never at operation
 *     level: a part of the running order scanned at a station doing another operation OF THAT SAME
 *     ORDER comes back ACTIVE, deliberately — the warning is about the wrong part, not the wrong
 *     operation, which the backend does not report today.
 *   - a tool is matched against the TOOL TABLE the machine publishes, and Sun never answers FOREIGN
 *     for one: the table is a sampled snapshot, so "not listed" may just mean the reading is stale,
 *     and blaming the operator for an outdated sample would be worse than saying nothing. The
 *     warning is therefore unreachable for a tool — the generic wording below only guards against a
 *     verdict the backend does not send today.
 *   - a fabrication order is compared against the ones the machine is running, which makes it the
 *     most direct verdict of the six: the scanned element and the context are the same kind of thing,
 *     so the warning can say plainly that this is not the order running here.
 *
 * The rest of the types keep the generic wording until their context rule is defined in Sun.
 */
export const ACTIVE_JOB_MISMATCH_KEY = 'The scanned element does not belong to the active job of this device'
export const LOCATION_MISMATCH_KEY = 'The scanned lot is not loaded in this machine'
export const FABRICATION_ORDER_MISMATCH_KEY = 'The scanned part does not belong to the fabrication order of this device'
export const RUNNING_ORDER_MISMATCH_KEY = 'The scanned fabrication order is not the one running on this device'

const CONTEXT_MISMATCHES = {
  RAWMATERIAL: { key: LOCATION_MISMATCH_KEY, namesFabricationOrder: false },
  PART: { key: FABRICATION_ORDER_MISMATCH_KEY, namesFabricationOrder: true },
  FABRICATIONORDER: { key: RUNNING_ORDER_MISMATCH_KEY, namesFabricationOrder: true }
}

const DEFAULT_CONTEXT_MISMATCH = { key: ACTIVE_JOB_MISMATCH_KEY, namesFabricationOrder: true }

/**
 * How to word a FOREIGN verdict for a type.
 *
 * @param {string} entityType in either form
 * @returns {{key: string, namesFabricationOrder: boolean}} `namesFabricationOrder` tells the caller
 *   whether appending the station's order code adds anything to the message.
 */
export const contextMismatch = (entityType) =>
  CONTEXT_MISMATCHES[wireEntityType(entityType)] || DEFAULT_CONTEXT_MISMATCH
